import React from 'react';
import PropTypes from 'prop-types';
import { FDEmptyState, FDPaper, FDStack } from '@facet-wealth/design';
import { useRefreshModalCollectionCleared } from 'Hooks/useRefreshEvent';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import { BodyTextMedium } from 'Packages/FacetDesign/Components';
import {
  useMobileLayoutForDashboardOnlyDoNotUseElsewhere,
} from 'Views/Page/Content/Templates/ClientDashboardPageTemplate/useMobileLayoutWithPillars';
import { ReturnToCurrentButton } from './ReturnToCurrentButton';
import { PillarStepper } from './PillarStepper';
import { FeaturedPillar } from '../FeaturedPillar';
import { Pillar } from '../Pillar';
import { PillarQueries } from './PillarQueries';

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride('pillarTitle', {
    _component: () => BodyTextMedium,
  }, overrideReadOnly);

const useStyles = buildUseStyles('PillarActions', (theme) => ({
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  backButtonDesktop: {
    position: 'absolute',
    top: theme.spacing(-1),
    transform: 'translateY(-100%)',
    right: 0,
  },
  backButtonMobile: {
    alignSelf: 'flex-end',
  },
  blocker: {
    position: 'absolute',
    top: theme.spacing(-1),
    left: theme.spacing(-1),
    padding: theme.spacing(1),
    boxSizing: 'content-box',
    height: '100%',
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.5)',
    zIndex: 10,
  },
}));

export const PillarActions = ({
  collection: pillarsCollection,
  queryCollection,
  onQuery,
  onModalClose,
  onClickActionItem,
  pageResource,
}) => {
  const isMobile = useMobileLayoutForDashboardOnlyDoNotUseElsewhere();
  const classes = useStyles();

  useRefreshModalCollectionCleared(() => {
    onModalClose();
  }, [onModalClose]);

  const returnToCurrentMonth = queryCollection.query(
    'getCurrentMonth',
    (link, { query }) => <ReturnToCurrentButton link={link} onSubmit={onQuery} query={query} />,
    () => null,
  );
  const returnToCurrentPillar = queryCollection.query(
    'getCurrentPillar',
    (link, { query }) => <ReturnToCurrentButton link={link} onSubmit={onQuery} query={query} />,
    () => null,
  );


  return (
    <div className={classes.container}>
      <FDStack
        width={isMobile ? '100%' : 450}
        maxWidth={isMobile ? 800 : undefined}
      >
        <FDStack direction={'column'} spacing={2} alignItems="center" width="100%">
          {returnToCurrentPillar || returnToCurrentMonth ? (
            <div className={isMobile ? classes.backButtonMobile : classes.backButtonDesktop}>
              {returnToCurrentMonth}
              {returnToCurrentPillar}
            </div>
          ) : null}
          <FDPaper
            appearance={'elevated'}
            height={isMobile ? undefined : '70vh'}
            width="100%"
            overflow="auto"
          >
            <FDStack paddingX={3} paddingY={2} minHeight="200px" width={'100%'} direction={'column'} justifyContent={'space-between'} spacing={2}>
              <FDStack direction={'column'} flex={1} spacing={2}>
                <FDStack alignItems={'center'} justifyContent={pageResource.isTruthy('prePlan') ? undefined : 'space-between'} wrap>
                  <MetaControl resource={pageResource} attrName={'pillarTitle'} metaOverrideSet={metaOverrideSet} />
                  <PillarQueries collection={queryCollection} onSubmit={onQuery} />
                </FDStack>
                <div className={classes.container}>
                  {pageResource.isTruthy('locked') ? (
                    <div className={classes.blocker} />
                  ) : null}
                  {pillarsCollection.resources().empty() ? (
                    <FDStack width="100%" justifyContent="center">
                      <FDEmptyState alignSelf={'center'} icon={'check'} title={'All set for this month'} />
                    </FDStack>
                  ) : null}
                  <FDStack flex={1} direction={'column'} spacing={4}>
                    {pillarsCollection.resources().list().map((resource, i) => {
                      const PillarComponent = (i === 0 && resource.isTruthy('completed') === false) ? FeaturedPillar : Pillar;

                      return (<PillarComponent
                        resource={resource}
                        key={resource.id()}
                        onClickActionItem={onClickActionItem}
                        prePlan={pageResource.isTruthy('prePlan')}
                      />);
                    })}
                  </FDStack>
                </div>
              </FDStack>
              <PillarStepper dashboardResource={pageResource} />
            </FDStack>
          </FDPaper>
        </FDStack>
      </FDStack>
    </div>
  );
};

PillarActions.defaultProps = {
  onModalClose: () => {},
};

PillarActions.propTypes = {
  pageResource: PropTypes.object.isRequired,
  collection: PropTypes.object.isRequired,
  queryCollection: PropTypes.object.isRequired,
  onQuery: PropTypes.func.isRequired,
  onClickActionItem: PropTypes.func.isRequired,
  onModalClose: PropTypes.func,
};
